/**
 * Algolia queries for job detail "similar jobs" cards and location hit count.
 * Used on SSR (serialized in page data) and on the client during CSR navigation.
 */
import type { SimilarJob } from '../../types/insights';
import { generateJobUrl } from '../routing/generate-url';
import { resolveJobUrlTitle, resolveJobUrlLocation } from '../routing/job-url-input';
import {
  buildAlgoliaLocationFacetFilter,
  buildAlgoliaNormalisedTitleFacetFilter,
} from '../search-router/build-quoted-facet-filter';
// Namespace import + unwrap: lambda/ has no "type": "module" (see server/utils/lambda-interop.ts).
import { unwrapLambdaModule } from '../../server/utils/lambda-interop';
import * as salaryRoleAliasesNs from '../../lambda/utils/salaryRoleAliases';
const { getSalaryRoleTitleVariants } = unwrapLambdaModule(salaryRoleAliasesNs);
import { companyProfileUrlPath } from '../routing/company-profile-url-path';
import { shouldLinkCompanyProfile } from '../company/should-link-company-profile';
import { resolveSearchHitIsTyomarkkinatori } from '../search/resolve-search-hit-is-tyomarkkinatori';
import { resolveSearchHitHostedLogoUrl } from '../search/resolve-search-hit-company-display';
import { TYOMARKKINATORI_PURPLE } from '../../components/Hit/utils/brandColorUtils';

export type SimilarJobsJobLocale = 'en' | 'fi' | 'sv';

export interface SimilarJobsAlgoliaParams {
  jobObjectId: string;
  normalisedTitle?: string | undefined;
  /** First location string (same as job detail primary location). */
  primaryLocation: string;
  jobLocale: SimilarJobsJobLocale;
  indexName: string;
  /** Base URL for absolute job links (e.g. https://www.jobcrawls.com). */
  baseUrl: string;
}

export interface SimilarJobsAlgoliaFiltersResult {
  combinedFilters: string;
  locationOnlyFilter: string | null;
}

export interface SimilarJobsSearchStrategy {
  label: 'title+location' | 'title-only';
  filters: string;
  query: string;
}

/**
 * Builds facet filters: exclude current job, optional normalised title, optional location.
 */
export function buildSimilarJobsAlgoliaFilters(params: {
  excludeObjectId: string;
  normalisedTitle?: string | undefined;
  primaryLocation: string;
}): SimilarJobsAlgoliaFiltersResult {
  const { excludeObjectId, normalisedTitle, primaryLocation } = params;
  const parts: string[] = [`NOT objectID:${excludeObjectId}`];
  const trimmedTitle = normalisedTitle?.trim();
  if (trimmedTitle) {
    // OR across every known synonym/leak variant of this role (see getSalaryRoleTitleVariants) —
    // the search index still stores each job's raw, un-merged title, so matching only the
    // current job's exact title would miss postings under a role-equivalent title.
    parts.push(buildAlgoliaNormalisedTitleFacetFilter(getSalaryRoleTitleVariants(trimmedTitle)));
  }
  const locationOnlyFilter = primaryLocation.trim()
    ? buildAlgoliaLocationFacetFilter([primaryLocation])
    : null;
  if (locationOnlyFilter) {
    parts.push(locationOnlyFilter);
  }
  return {
    combinedFilters: parts.join(' AND '),
    locationOnlyFilter,
  };
}

/**
 * Build strict matching tiers to avoid broad "not really similar" results.
 * Requires normalisedTitle; if missing we return no strategies (better empty than irrelevant).
 */
export function buildSimilarJobsSearchStrategies(params: {
  excludeObjectId: string;
  normalisedTitle?: string | undefined;
  primaryLocation: string;
}): SimilarJobsSearchStrategy[] {
  const trimmedTitle = params.normalisedTitle?.trim();
  if (!trimmedTitle) return [];

  // Same broadened, OR-across-variants matching as buildSimilarJobsAlgoliaFilters above.
  const titleFilter = buildAlgoliaNormalisedTitleFacetFilter(getSalaryRoleTitleVariants(trimmedTitle));
  const base = `NOT objectID:${params.excludeObjectId} AND ${titleFilter}`;
  const locationFilter = params.primaryLocation.trim()
    ? buildAlgoliaLocationFacetFilter([params.primaryLocation])
    : null;

  const strategies: SimilarJobsSearchStrategy[] = [];
  if (locationFilter) {
    strategies.push({
      label: 'title+location',
      filters: `${base} AND ${locationFilter}`,
      query: '',
    });
  }
  strategies.push({
    label: 'title-only',
    filters: base,
    query: '',
  });
  return strategies;
}

/** Attributes needed only for SimilarJobsSection cards + job URLs. */
export function attributesToRetrieveForSimilarJobCards(jobLocale: SimilarJobsJobLocale): string[] {
  return [
    'objectID',
    'title',
    `title_${jobLocale}`,
    'normalisedTitle',
    'companyId',
    'companyName',
    'location',
    'salaryRangeMin',
    'salaryRangeMax',
    'currency',
    'salaryPeriod',
    'brandColour',
    'logoUrl',
    'registryCompanyFormDescription',
    'isTyomarkkinatori',
    'remoteness',
    'jobType',
    'normalisedRoleLevel',
    'requiredLanguages',
  ];
}

export function mapAlgoliaHitToSimilarJobCard(
  hit: Record<string, unknown>,
  jobLocale: SimilarJobsJobLocale,
  baseUrl: string,
): SimilarJob | null {
  const objectID = hit.objectID != null ? String(hit.objectID) : '';
  if (!objectID) return null;
  const title = (hit[`title_${jobLocale}`] as string | undefined) || (hit.title as string | undefined);
  const companyId = hit.companyId as string | undefined;
  const companyName = typeof hit.companyName === 'string' ? hit.companyName : undefined;
  const location = hit.location as string | string[] | undefined;

  const isTyomarkkinatori = resolveSearchHitIsTyomarkkinatori(hit);
  const rawBrandColour = typeof hit.brandColour === 'string' ? hit.brandColour : undefined;
  const brandColour = isTyomarkkinatori ? TYOMARKKINATORI_PURPLE : rawBrandColour;
  const hostedLogoUrl = resolveSearchHitHostedLogoUrl(hit);
  const registryCompanyFormDescription =
    typeof hit.registryCompanyFormDescription === 'string' ? hit.registryCompanyFormDescription : undefined;
  const canLinkCompanyProfile = shouldLinkCompanyProfile({ companyId, isTyomarkkinatori });

  const remoteness = typeof hit.remoteness === 'string' ? hit.remoteness : undefined;
  const jobType = typeof hit.jobType === 'string' ? hit.jobType : undefined;
  const rawRoleLevel = hit.normalisedRoleLevel;
  const normalisedRoleLevel = Array.isArray(rawRoleLevel)
    ? (rawRoleLevel as unknown[]).filter((v): v is string => typeof v === 'string' && v.trim().length > 0).join(', ')
    : typeof rawRoleLevel === 'string' ? rawRoleLevel : undefined;
  const requiredLanguages = Array.isArray(hit.requiredLanguages)
    ? (hit.requiredLanguages as unknown[]).filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    : undefined;

  return {
    objectID,
    title,
    normalisedTitle: hit.normalisedTitle as string | undefined,
    companyId,
    companyName,
    location,
    salaryRangeMin: hit.salaryRangeMin as number | undefined,
    salaryRangeMax: hit.salaryRangeMax as number | undefined,
    currency: hit.currency as string | undefined,
    salaryPeriod: hit.salaryPeriod as string | undefined,
    brandColour,
    hostedLogoUrl,
    registryCompanyFormDescription,
    isTyomarkkinatori,
    companyProfileUrl: canLinkCompanyProfile && companyId ? companyProfileUrlPath(jobLocale, companyId) : undefined,
    remoteness,
    jobType,
    normalisedRoleLevel,
    requiredLanguages,
    url: generateJobUrl({
      baseUrl,
      jobId: objectID,
      locale: jobLocale,
      company: companyId,
      title: resolveJobUrlTitle({
        normalisedTitle: hit.normalisedTitle as string | undefined,
        title,
      }),
      location: resolveJobUrlLocation({ location }),
    }),
  };
}

export function getAlgoliaSearchCredentialsFromEnv(): { appId: string; apiKey: string } | null {
  const appIdFromVite =
    (import.meta.env.PUBLIC_ENV__ALGOLIA_APP_ID as string | undefined) ||
    (import.meta.env.VITE_PUBLIC_ENV__ALGOLIA_APP_ID as string | undefined);
  const searchKeyFromVite = import.meta.env.PUBLIC_ENV__ALGOLIA_SEARCH_KEY as string | undefined;
  const appId =
    (typeof process !== 'undefined' && process.env?.PUBLIC_ENV__ALGOLIA_APP_ID) ||
    appIdFromVite;
  const apiKey =
    (typeof process !== 'undefined' &&
      (process.env?.ALGOLIA_SEARCH_KEY || process.env?.PUBLIC_ENV__ALGOLIA_SEARCH_KEY)) ||
    searchKeyFromVite;
  if (!appId || !apiKey) return null;
  return { appId: String(appId), apiKey: String(apiKey) };
}

interface SimilarJobsSearchResultLike {
  hits?: Array<Record<string, unknown>>;
  nbHits?: number;
}

/**
 * Run one similar-jobs query through the search-provider abstraction:
 * the browser client on the client, and performSsrJobSearch on the server.
 * The Algolia-style `filters` string is translated to Typesense `filter_by`
 * inside the abstraction.
 */
async function runSimilarJobsSearch(
  indexName: string,
  params: { query: string; filters?: string | null; hitsPerPage: number },
): Promise<SimilarJobsSearchResultLike | null> {
  const searchParams = {
    query: params.query,
    hitsPerPage: params.hitsPerPage,
    ...(params.filters ? { filters: params.filters } : {}),
  };

  if (typeof window !== 'undefined') {
    const { createBrowserSearchClient } = await import('../search/create-browser-search-client');
    const client = createBrowserSearchClient();
    if (!client) {
      console.log('[SimilarJobsDebug] client-init-skipped', { reason: 'no_browser_search_client' });
      return null;
    }
    const res = await client.search([{ indexName, params: searchParams }]);
    return ((res as { results?: SimilarJobsSearchResultLike[] })?.results?.[0]) ?? null;
  }

  const { performSsrJobSearch } = await import('../search/perform-ssr-job-search');
  return performSsrJobSearch({
    indexName,
    params: searchParams,
  });
}

export async function fetchSimilarJobsAndLocationCountFromAlgolia(
  params: SimilarJobsAlgoliaParams,
): Promise<{ similarJobs: SimilarJob[]; locationJobCount: number | null }> {
  const { locationOnlyFilter } = buildSimilarJobsAlgoliaFilters({
    excludeObjectId: params.jobObjectId,
    normalisedTitle: params.normalisedTitle,
    primaryLocation: params.primaryLocation,
  });
  const searchStrategies = buildSimilarJobsSearchStrategies({
    excludeObjectId: params.jobObjectId,
    normalisedTitle: params.normalisedTitle,
    primaryLocation: params.primaryLocation,
  });
  if (searchStrategies.length === 0) {
    console.log('[SimilarJobsDebug][algolia] query-skipped', {
      objectId: params.jobObjectId,
      reason: 'missing_normalised_title',
    });
    return { similarJobs: [], locationJobCount: null };
  }

  if (typeof window !== 'undefined') {
    console.log('[SimilarJobsDebug] query-start', {
      objectId: params.jobObjectId,
      indexName: params.indexName,
      jobLocale: params.jobLocale,
      hasTitleFilter: !!params.normalisedTitle?.trim(),
      hasLocationFilter: !!params.primaryLocation?.trim(),
      strategyLabels: searchStrategies.map((s) => s.label),
    });
  }

  const locationPromise = runSimilarJobsSearch(
    params.indexName,
    { query: '', filters: locationOnlyFilter, hitsPerPage: 0 },
  );

  let chosenStrategy: SimilarJobsSearchStrategy['label'] | null = null;
  let similarResult: PromiseSettledResult<any> = { status: 'fulfilled', value: { hits: [] } };
  for (const strategy of searchStrategies) {
    const candidate = await Promise.allSettled([
      runSimilarJobsSearch(
        params.indexName,
        { query: strategy.query, filters: strategy.filters, hitsPerPage: 8 },
      ),
    ]);
    similarResult = candidate[0];
    if (similarResult.status === 'fulfilled' && Array.isArray(similarResult.value?.hits) && similarResult.value.hits.length > 0) {
      chosenStrategy = strategy.label;
      break;
    }
  }
  const [locationResult] = await Promise.allSettled([locationPromise]);

  if (typeof window !== 'undefined') {
    console.log('[SimilarJobsDebug][algolia] query-finished', {
      objectId: params.jobObjectId,
      strategyUsed: chosenStrategy,
      similarStatus: similarResult.status,
      locationStatus: locationResult.status,
      similarReason: similarResult.status === 'rejected' ? String(similarResult.reason) : null,
      locationReason: locationResult.status === 'rejected' ? String(locationResult.reason) : null,
    });
  }

  let similarJobs: SimilarJob[] = [];
  let locationJobCount: number | null = null;

  if (similarResult.status === 'fulfilled') {
    const hits = similarResult.value?.hits ?? [];
    for (const h of hits) {
      const card = mapAlgoliaHitToSimilarJobCard(h as Record<string, unknown>, params.jobLocale, params.baseUrl);
      if (card) similarJobs.push(card);
    }
  }

  if (locationResult.status === 'fulfilled') {
    locationJobCount = locationResult.value?.nbHits ?? null;
  }

  return { similarJobs, locationJobCount };
}
